import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react'

import type { ActionsMeta } from '@/api/actions'

import { issuesAt, type Issue } from './issues'
import { DEFAULT_META } from './meta'

/**
 * What every field of the manual editor needs without prop-drilling: the vocabulary, the
 * participants in scope (their names feed every "who" picker and ref suggestion), the current
 * issues (each field shows its own), the other actions (fallback picker) and the ids of the
 * suggestion lists the editor renders once for all its fields.
 *
 * The value only changes when one of those really does (see `useStable`), so typing in one part of
 * the form does not re-render the others.
 */

export interface ActionChoice {
  id: string
  label: string
  roles: string[]
}

/** `<datalist>` ids shared by every field of one editor (rendered once by the editor). */
export interface SharedLists {
  capabilities: string
  clips: string
  refs: string
  refsWithFaces: string
  roleRefs: string
  events: string
  stateKeys: string
}

export interface EditorContextValue {
  kind: 'action' | 'rule'
  meta: ActionsMeta
  meanings: Record<string, string>
  roles: string[]
  errors: Issue[]
  warnings: Issue[]
  actions: ActionChoice[]
  /** The document's own id (a fallback cannot be itself). */
  selfId: string
  lists?: SharedLists
}

const Ctx = createContext<EditorContextValue>({
  kind: 'action',
  meta: DEFAULT_META,
  meanings: {},
  roles: [],
  errors: [],
  warnings: [],
  actions: [],
  selfId: '',
})

export function EditorProvider({ value, children }: { value: EditorContextValue; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useEditor(): EditorContextValue {
  return useContext(Ctx)
}

/** The issues a field at `path` shows: at it, and (for leaf fields, `deep`) anything under it. */
export function useIssues(path: string, deep = true): { errors: Issue[]; warnings: Issue[] } {
  const { errors, warnings } = useEditor()
  return useMemo(() => ({ errors: issuesAt(errors, path, deep), warnings: issuesAt(warnings, path, deep) }), [errors, warnings, path, deep])
}

/** Override the participants in scope (a rule's steps use `subject` / `other`). */
export function RolesScope({ roles, children }: { roles: string[]; children: ReactNode }) {
  const parent = useEditor()
  const value = useMemo(() => ({ ...parent, roles }), [parent, roles])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** The same object for as long as its JSON is the same (keeps memoised parts from re-rendering). */
export function useStable<T>(value: T): T {
  const ref = useRef<{ key: string; value: T } | null>(null)
  const key = JSON.stringify(value)
  if (!ref.current || ref.current.key !== key) ref.current = { key, value }
  return ref.current.value
}
